/**
 * AdminRoutes.jsx — institution-admin dashboard routes.
 * Shaped by prompt 35; MKT-05; G2e owner 2026-10-12.
 * Invariants: alias/redirect only; Fusion Founder accounting uses the same
 *   FoundingPartnerAccountingGate as the old path. Settings and PageGuide stay
 *   ungated here. MasterChart and SavoirCare follow the launcher catalog.
 *   Interpreter Requests and CareVerify use useOrgAdminAccess.
 * Related: fusionFounderAccountingRoutes.jsx, RequireLauncherApp.jsx, useOrgAdminAccess.js.
 */
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Alert, Box, CircularProgress, Container } from "@mui/material";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import PageGuideAdminPage from "pages/dashboard/admin/pageGuide/PageGuideAdminPage";
import AllFilesPage from "pages/dashboard/admin/AllFilesPage";
import TeamPage from "pages/dashboard/admin/TeamPage";
import UsagePage from "pages/dashboard/admin/UsagePage";
import AdminOnboarding from "pages/dashboard/admin/AdminOnboarding";
import DataSharingSettingsPage from "pages/dashboard/admin/settings/DataSharingSettingsPage";
import SupportAccessSettingsPage from "pages/dashboard/admin/settings/SupportAccessSettingsPage";
import OrgDataExportPage from "pages/dashboard/admin/settings/OrgDataExportPage";
import AnalyticsPage from "pages/dashboard/admin/AnalyticsPage";
import LoopAnalyticsPage from "pages/dashboard/admin/LoopAnalyticsPage";
import ReadinessDashboard from "pages/dashboard/readiness/ReadinessDashboard";
import AuditLogPage from "pages/dashboard/audit/AuditLogPage";
import InstitutionReportsPage from "pages/dashboard/reports/InstitutionReportsPage";
import ZipSlotBiddingPage from "pages/dashboard/zipSlotAds/ZipSlotBiddingPage";
import AdBalancePage from "pages/dashboard/zipSlotAds/AdBalancePage";
import AdPerformancePage from "pages/dashboard/zipSlotAds/AdPerformancePage";
import VideoLibraryPage from "pages/dashboard/institutionRollout/VideoLibraryPage";
import InstitutionBrandPage from "pages/dashboard/institutionRollout/InstitutionBrandPage";
import DistributePage from "pages/dashboard/institutionRollout/DistributePage";
import IpaSubmissionPage from "pages/dashboard/admin/ipa/IpaSubmissionPage";
import InstitutionSetupPage from "pages/onboarding/InstitutionSetupPage";
import TciSetupPage from "pages/dashboard/admin/TciSetupPage";
import FdeCOnboardingPage from "pages/onboarding/FdeCOnboardingPage";
import TciManagerOnboardingPage from "pages/onboarding/TciManagerOnboardingPage";
import InstitutionLeaderOnboardingPage from "pages/onboarding/InstitutionLeaderOnboardingPage";
import SessionListPage from "pages/dashboard/sessions/SessionListPage";
import SessionReplayPage from "pages/dashboard/sessions/SessionReplayPage";
import CandidateQueuePage from "pages/dashboard/prompts/CandidateQueuePage";
import FdeRosterPage from "pages/dashboard/admin/fde/FdeRosterPage";
import LocationHierarchyPage from "pages/dashboard/admin/LocationHierarchyPage";
import DomainManagementPage from "pages/dashboard/admin/domains/DomainManagementPage";
import TciManagerDashboard from "pages/dashboard/tcimanager/TciManagerDashboard";
import ManageTcisPage from "pages/dashboard/tcimanager/ManageTcisPage";
import TciPromptQueryManagerPage from "pages/dashboard/tcimanager/TciPromptQueryManagerPage";
import CandidateReviewPage from "pages/dashboard/tcimanager/CandidateReviewPage";
import TciManagerMemberPage from "pages/dashboard/tcimanager/TciManagerMemberPage";
import GlobalTciManagerDashboard from "pages/dashboard/global-tci-manager/GlobalTciManagerDashboard";
import TciInterrogatoryPage from "pages/dashboard/tci/TciInterrogatoryPage";
import TciGovernanceView from "pages/dashboard/tci/TciGovernanceView";
import TrainingDeckOverridePage from "pages/dashboard/training/TrainingDeckOverridePage";
import Settings from "components/dashboard/Settings";
import OrgAdminGate from "components/common/OrgAdminGate";
import ZipSlotAdsGate from "components/common/ZipSlotAdsGate";
import { fusionFounderAccountingRouteElements } from "routes/fusionFounderAccountingRoutes";
import RetiredChartMindRoute from "components/common/RetiredChartMindRoute";
import ChartMindPlusPage from "pages/chartmindPlus/ChartMindPlusPage";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import ChartMindPlusDifferentialPage from "pages/chartmindPlus/ChartMindPlusDifferentialPage";
import PeerViewPage from "pages/dashboard/professional/PeerViewPage";
// MasterChart and SavoirCare are mirrored from other role trees. G2e wraps
// them in the launcher catalog, which is No for everyone who lands here.
import { MasterChartDashboardRoutes } from "routes/MasterChartDashboardRoutes";
import { SavoirCareBoardPage } from "features/savoirCare";
import InterpreterRequestsPage from "pages/dashboard/facility/InterpreterRequestsPage";
import FacilityAvailabilityPage from "pages/dashboard/careverify/FacilityAvailabilityPage";
import FacilityAttestationDashboardPage from "pages/dashboard/careverify/FacilityAttestationDashboardPage";
import MisdirectionTriagePage from "pages/dashboard/careverify/MisdirectionTriagePage";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import { useFdeRole } from "hooks/useFdeRole";
import { useGlobalTciManager } from "hooks/useGlobalTciManager";
import {
  needsFdeCOrientation,
  needsTciManagerOrientation,
} from "services/fde/roleOnboardingService";
import isDevAuthFirestoreUser from "utils/isDevAuthFirestoreUser";
import DashboardLayout from "components/dashboard/DashboardLayout";
import TciHappyPathChecklist from "components/dashboard/TciHappyPathChecklist";
import { PromptsManagerProvider } from "context/PromptsManager";
import { ChartMindSessionProvider } from "context/ChartMindSession";

const DENIED_APP = "This app is not available for your account.";

/**
 * Routes only accept Route elements, so the shared MasterChart fragment is
 * cloned here instead of wrapping the helper. /health-records is inside that
 * fragment. A catalog that does not list masterchart sees the launcher denial.
 */
function guardMasterChartRoutes(fragment) {
  const children = fragment?.props?.children;
  if (!children) return fragment ?? null;
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, {
      element: (
        <RequireLauncherApp appId="masterchart">
          {child.props.element}
        </RequireLauncherApp>
      ),
    });
  });
}

/**
 * Same rule as OrgAdminGate (useOrgAdminAccess). The denial copy matches
 * RequireLauncherApp because these URLs are opened like launcher apps.
 */
function RequireInstitutionAdmin({ children }) {
  const { canAccess } = useOrgAdminAccess();
  if (!canAccess) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info" data-testid="launcher.guard.denied">
          {DENIED_APP}
        </Alert>
      </Container>
    );
  }
  return children;
}

/**
 * AdminRoutes - Routes for ChartMind Manager (admin role)
 *
 * Flow:
 * - No subscription AND not beta admin → Full-screen onboarding (purchase seats)
 * - Has subscription OR is beta admin → Dashboard with Prompts + Organization
 */
const AdminRoutes = () => {
  const {
    user,
    subscription,
    subscriptionData,
    isBetaAdmin,
    organizationLoading,
    tokenClaims,
    userData,
    userLoading,
  } = useAuth();
  const { landingPath, loading: fdeLoading, isFdeDc, isFdeC } = useFdeRole();
  const { isGlobalTciManager, loading: gtmLoading } = useGlobalTciManager();
  const hasGtmAccess = isGlobalTciManager || Boolean(tokenClaims?.globalTciManager);
  const location = useLocation();
  const path = location.pathname;
  const isOnTciManagerOnboarding = path.includes("/onboarding/tci-manager");
  const isOnFdeCOnboarding = path.includes("/onboarding/fde-c");
  const isChartMindSessionList = /\/chartmind\/sessions\/?$/.test(path);
  const chartMindFullWidth =
    (path.includes("/chartmind") || path.includes("/peerview")) &&
    !isChartMindSessionList;
  const hasActiveSubscription = subscriptionData?.status === "active";

  const hasDevDashboardAccess = isDevAuthFirestoreUser(userData);

  const routeShellLoading =
    organizationLoading || fdeLoading || gtmLoading || userLoading || userData === undefined;

  // Wait for org / FDE / profile before deciding — show dashboard chrome, not a blank page.
  if (routeShellLoading) {
    return (
      <DashboardLayout fullWidth={chartMindFullWidth}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (
    isFdeDc &&
    needsTciManagerOrientation(userData) &&
    !isOnTciManagerOnboarding
  ) {
    return <Navigate to="/dashboard/onboarding/tci-manager" replace />;
  }

  if (
    isFdeC &&
    !isFdeDc &&
    needsFdeCOrientation(userData) &&
    !isOnFdeCOnboarding
  ) {
    return <Navigate to="/dashboard/onboarding/fde-c" replace />;
  }

  // [BETA-KIJABE-ADMIN] Beta admin orgs skip subscription requirement entirely.
  // Global TCI Managers may operate without an org subscription.
  // Dev-auth demo accounts (@dev.local) skip seat purchase onboarding for local walkthroughs.
  if (!hasActiveSubscription && !isBetaAdmin && !hasGtmAccess && !hasDevDashboardAccess) {
    return <AdminOnboarding />;
  }

  // Normal dashboard with sidebar
  return (
    <DashboardLayout fullWidth={chartMindFullWidth}>
      <TciHappyPathChecklist />
      <PromptsManagerProvider>
        <ChartMindSessionProvider>
          <Routes>
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    landingPath ||
                    (hasGtmAccess ? "/dashboard/global-tci-manager" : "/dashboard/prompts")
                  }
                  replace
                />
              }
            />
            <Route path="/prompts" element={<PromptsManagerPage />} />
            {fusionFounderAccountingRouteElements()}
            <Route path="/page-guide" element={<PageGuideAdminPage />} />
            <Route path="/tci-setup" element={<TciSetupPage />} />
            <Route path="/fde-roster" element={<FdeRosterPage />} />
            <Route path="/location-hierarchy" element={<LocationHierarchyPage />} />
            <Route path="/domains" element={<DomainManagementPage />} />
            <Route path="/tci-governance/new" element={<TciInterrogatoryPage />} />
            <Route path="/tci-governance/:tciId" element={<TciGovernanceView />} />
            <Route path="/tci-manager" element={<TciManagerDashboard />} />
            <Route path="/tci-manager/tcis" element={<ManageTcisPage />} />
            <Route path="/tci-manager/tcis/:tciId" element={<ManageTcisPage />} />
            <Route
              path="/tci-manager/tcis/:tciId/prompts/:promptId"
              element={<TciPromptQueryManagerPage />}
            />
            <Route path="/tci-manager/candidates/:candidateId" element={<CandidateReviewPage />} />
            <Route path="/tci-manager/team/:memberUid" element={<TciManagerMemberPage />} />
            <Route path="/training-decks/:tciId" element={<TrainingDeckOverridePage />} />
            <Route path="/global-tci-manager" element={<GlobalTciManagerDashboard />} />
            <Route path="/files" element={<AllFilesPage />} />
            <Route path="/diskusage" element={<Navigate to="/dashboard/files" replace />} />
            <Route path="/team" element={<TeamPage />} />
            {/* [BETA-KIJABE-ADMIN] Usage page hidden from beta admin orgs (no token tracking). */}
            {!isBetaAdmin && <Route path="/usage" element={<UsagePage />} />}
            <Route
              path="/settings"
              element={<Settings uid={user.uid} subscription={subscription} />}
            />
            <Route path="/settings/data-sharing" element={<DataSharingSettingsPage />} />
            <Route path="/settings/support-access" element={<SupportAccessSettingsPage />} />
            <Route path="/settings/export-data" element={<OrgDataExportPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/loop-analytics" element={<LoopAnalyticsPage />} />
            <Route path="/readiness" element={<ReadinessDashboard />} />
            {/* G2e: launcher catalog. Institution Admin, FDE flags, and DSCR admin are No. */}
            {guardMasterChartRoutes(MasterChartDashboardRoutes())}
            <Route
              path="/savoircare"
              element={
                <RequireLauncherApp appId="savoircare">
                  <SavoirCareBoardPage />
                </RequireLauncherApp>
              }
            />
            <Route
              path="/interpreter-requests"
              element={
                <RequireInstitutionAdmin>
                  <InterpreterRequestsPage />
                </RequireInstitutionAdmin>
              }
            />
            <Route
              path="/careverify/availability"
              element={
                <RequireInstitutionAdmin>
                  <FacilityAvailabilityPage />
                </RequireInstitutionAdmin>
              }
            />
            <Route
              path="/careverify/attestation"
              element={
                <RequireInstitutionAdmin>
                  <FacilityAttestationDashboardPage />
                </RequireInstitutionAdmin>
              }
            />
            <Route
              path="/careverify/misdirection-triage"
              element={
                <RequireInstitutionAdmin>
                  <MisdirectionTriagePage />
                </RequireInstitutionAdmin>
              }
            />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/reports" element={<InstitutionReportsPage />} />
            <Route
              path="/advertising/slots"
              element={
                <ZipSlotAdsGate>
                  <ZipSlotBiddingPage />
                </ZipSlotAdsGate>
              }
            />
            <Route
              path="/advertising/balance"
              element={
                <ZipSlotAdsGate>
                  <AdBalancePage />
                </ZipSlotAdsGate>
              }
            />
            <Route
              path="/advertising/performance"
              element={
                <ZipSlotAdsGate>
                  <AdPerformancePage />
                </ZipSlotAdsGate>
              }
            />
            <Route path="/institution-rollout" element={<VideoLibraryPage />} />
            <Route
              path="/institution-rollout/brand"
              element={<InstitutionBrandPage />}
            />
            <Route
              path="/institution-rollout/distribute"
              element={<DistributePage />}
            />
            <Route path="/ipa" element={<IpaSubmissionPage />} />
            <Route path="/setup/institution" element={<InstitutionSetupPage />} />
            <Route path="/onboarding/fde-c" element={<FdeCOnboardingPage />} />
            <Route path="/onboarding/tci-manager" element={<TciManagerOnboardingPage />} />
            <Route
              path="/onboarding/institution-leader"
              element={<InstitutionLeaderOnboardingPage />}
            />
            <Route path="/sessions" element={<SessionListPage />} />
            <Route path="/sessions/:sessionId" element={<SessionReplayPage />} />
            <Route path="/prompts/candidates" element={<CandidateQueuePage />} />
            <Route
              path="/chartmind"
              element={
                <OrgAdminGate>
                  <RetiredChartMindRoute />
                </OrgAdminGate>
              }
            />
            <Route
              path="/chartmind-plus"
              element={
                <RequireLauncherApp appId="chartmind-plus">
                  <OrgAdminGate>
                    <ChartMindPlusPage />
                  </OrgAdminGate>
                </RequireLauncherApp>
              }
            />
            <Route
              path="/chartmind-plus/differential"
              element={
                <RequireLauncherApp appId="chartmind-plus">
                  <OrgAdminGate>
                    <ChartMindPlusDifferentialPage />
                  </OrgAdminGate>
                </RequireLauncherApp>
              }
            />
            <Route
              path="/chartmind/sessions"
              element={
                <OrgAdminGate>
                  <RetiredChartMindRoute />
                </OrgAdminGate>
              }
            />
            <Route
              path="/peerview"
              element={
                <OrgAdminGate>
                  <PeerViewPage />
                </OrgAdminGate>
              }
            />
            <Route
              path="/chartmind/:sessionId"
              element={
                <OrgAdminGate>
                  <RetiredChartMindRoute />
                </OrgAdminGate>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard/prompts" />} />
          </Routes>
        </ChartMindSessionProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default AdminRoutes;
